import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import { Link, Navigate, Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import AdverseMediaIdentityMatchDetails from 'dashboard/components/ReportResult/AdverseMediaIdentityMatchDetails';
import AdverseMediaIdentityMatchSummary from 'dashboard/components/ReportResult/AdverseMediaIdentityMatchSummary';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import { MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { ReportBusinessAdverseMedia } from 'dashboard/models/report/business-adverse-media';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const MatchDetailsLink = styled(Link)`
  &:not(:last-of-type) > div {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

interface BusinessAdverseMediaProps {
  report: ReportBusinessAdverseMedia;
  isRedacted: boolean;
  multiCardFormat?: boolean;
}

const BusinessAdverseMedia = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: BusinessAdverseMediaProps) => {
  const {
    'related-sources': relatedSources = [],
    'ignore-list-entity': ignoreListEntity = {},
    'ignore-list-media': ignoreListMedia = {},
  } = report.attributes;

  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);

  // filter to only entities that are active (i.e. not in ignore list) and no active media hits (i.e. not in relatedSources)
  const activeWithIgnoredMediaKeys = Object.keys(ignoreListMedia).filter(
    (id) =>
      !Object.keys(ignoreListEntity).includes(id) &&
      !relatedSources.map((source) => source.id).includes(id),
  );

  const activeMatches = relatedSources
    .concat(
      (activeWithIgnoredMediaKeys ?? []).map((key) => {
        return { ...ignoreListMedia[key][0].match, id: key, media: [] };
      }),
    )
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    .sort((a, b) => (a.id < b.id ? 1 : -1));

  const [ignoreEntityModal, { enable: showIgnoreEntityModal, disable: hideIgnoreEntityModal }] =
    useToggle(false);

  const ignoredMatches = Object.values(ignoreListEntity).map((entry) => entry.match);

  // No matches, no ignored, then show no matches page
  if (
    relatedSources.length === 0 &&
    Object.keys(ignoreListEntity).length === 0 &&
    Object.keys(ignoreListMedia).length === 0
  ) {
    return (
      <ReportResultLayout multiCardFormat={multiCardFormat}>
        <MatchIconPositive label='No matches found.' />
      </ReportResultLayout>
    );
  }

  // 1 match and no ignored, then show match details
  else if (activeMatches.length === 1 && Object.keys(ignoreListEntity).length === 0) {
    return (
      <AdverseMediaIdentityMatchDetails
        relatedSource={activeMatches[0]}
        multiCardFormat={multiCardFormat}
        isRedacted={isRedacted}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ignoreListMedia={ignoreListMedia[activeMatches[0].id]}
        report={report}
      />
    );
  }

  // else, show tabbed container with active and dismissed matches
  // note: tabs will not show up if nothing has been dismissed
  return (
    <SentryRoutes>
      {activeMatches.map((relatedSource, idx) => (
        <Route
          path={`identity-matches/active/${idx}`}
          key={idx}
          element={
            <AdverseMediaIdentityMatchDetails
              key={idx}
              relatedSource={relatedSource}
              multiCardFormat={multiCardFormat}
              isRedacted={isRedacted}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ignoreListMedia={ignoreListMedia[relatedSource.id]}
              report={report}
            />
          }
        />
      ))}

      {ignoredMatches.map((relatedSource, idx) => (
        <Route
          path={`identity-matches/dismissed/${idx}`}
          key={idx}
          element={
            <AdverseMediaIdentityMatchDetails
              key={idx}
              relatedSource={relatedSource}
              multiCardFormat={multiCardFormat}
              isRedacted={isRedacted}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ignoreListMedia={ignoreListMedia[relatedSource.id]}
              isIgnoredEntity={true}
              report={report}
            />
          }
        />
      ))}
      <Route
        index
        element={
          <ReportResultLayout multiCardFormat={multiCardFormat}>
            <>
              <TitleContainer>
                {multiCardFormat && <Card.Title>Identity matches</Card.Title>}
                <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
                  <ContextMenu
                    variant='inverse'
                    items={[
                      {
                        id: 'reports-ignore-hits-modal__dismiss-all-hits-button',
                        onClick: showIgnoreEntityModal,
                        hidden: false,
                        label: 'Dismiss all identity matches',
                      },
                    ]}
                    renderItem={(item) => (
                      <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                        {item.label}
                      </ContextMenu.Item>
                    )}
                  />
                  <IgnoreHitsModal
                    ignoreHitsModalOpen={ignoreEntityModal}
                    hideIgnoreHitsModal={hideIgnoreEntityModal}
                    modalHeaderText='Dismiss Identity Matches?'
                    modalDescriptionText='Dismissing all identity matches will prevent all media for these identities from appearing in future report runs.'
                    report={report}
                    ignoreType='all'
                  />
                </Permissioned>
              </TitleContainer>
              {ignoredMatches.length > 0 && (
                <>
                  <TabBar>
                    <Tab
                      id='report-business-adverse-media__entity-tab-active'
                      selected={isActiveTabSelected}
                      onClick={() => {
                        setIsActiveTabSelected(true);
                      }}
                    >
                      <TabText>
                        <span>Active</span>
                        <Sticker>{activeMatches.length}</Sticker>
                      </TabText>
                    </Tab>
                    <Tab
                      id='report-business-adverse-media__entity-tab-ignored'
                      selected={!isActiveTabSelected}
                      onClick={() => {
                        setIsActiveTabSelected(false);
                      }}
                    >
                      <TabText>
                        <span>Dismissed</span>
                        <Sticker>{ignoredMatches.length}</Sticker>
                      </TabText>
                    </Tab>
                  </TabBar>
                  <Spacer height={1.5} />
                </>
              )}
              {isActiveTabSelected ? (
                <>
                  {activeMatches.length === 0 ? (
                    <MatchIconPositive label='No matches found.' />
                  ) : (
                    activeMatches.map((relatedSource, idx) => (
                      <MatchDetailsLink
                        key={idx}
                        to={`/dashboard/reports/${report.id}/identity-matches/active/${idx}`}
                        state={{ internalRedirect: true }}
                      >
                        <AdverseMediaIdentityMatchSummary relatedSource={relatedSource} />
                      </MatchDetailsLink>
                    ))
                  )}
                </>
              ) : (
                <>
                  {ignoredMatches.map((relatedSource, idx) => (
                    <MatchDetailsLink
                      key={idx}
                      to={`/dashboard/reports/${report.id}/identity-matches/dismissed/${idx}`}
                      state={{ internalRedirect: true }}
                    >
                      <AdverseMediaIdentityMatchSummary
                        relatedSource={relatedSource}
                        isDismissed={true}
                      />
                    </MatchDetailsLink>
                  ))}
                </>
              )}
            </>
          </ReportResultLayout>
        }
      />
      <Route path='*' element={<Navigate to='.' replace />} />
    </SentryRoutes>
  );
};

export default BusinessAdverseMedia;
